import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const AutoReplyRuleSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").required("Obrigatório"),
  replyMessage: Yup.string().min(2, "Muito curta").required("Obrigatório"),
});

const keywordsToText = keywords => (keywords || []).join("\n");
const textToKeywords = text =>
  (text || "")
    .split("\n")
    .map(k => k.trim())
    .filter(k => k.length > 0);

const AutoReplyRuleModal = ({ open, onClose, ruleId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    replyMessage: "",
    blockContact: false,
    active: true,
    keywordsText: "",
  };

  const [rule, setRule] = useState(initialState);

  useEffect(() => {
    const fetchRule = async () => {
      if (!ruleId) {
        setRule(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/auto-reply-rules/${ruleId}`);
        setRule({
          name: data.name,
          replyMessage: data.replyMessage,
          blockContact: !!data.blockContact,
          active: data.active,
          keywordsText: keywordsToText(data.keywords),
        });
      } catch (err) {
        toastError(err);
      }
    };
    fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, open]);

  const handleClose = () => {
    onClose();
    setRule(initialState);
  };

  const handleSaveRule = async values => {
    const finalKeywords = textToKeywords(values.keywordsText);

    if (finalKeywords.length === 0) {
      toast.error("Adicione ao menos uma palavra ou frase.");
      return;
    }

    const { keywordsText, ...rest } = values;
    const ruleData = {
      ...rest,
      keywords: finalKeywords,
    };

    try {
      if (ruleId) {
        await api.put(`/auto-reply-rules/${ruleId}`, ruleData);
      } else {
        await api.post("/auto-reply-rules", ruleData);
      }
      toast.success("Regra de resposta automática salva com sucesso!");
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {ruleId ? "Editar resposta automática" : "Nova resposta automática"}
        </DialogTitle>
        <Formik
          initialValues={rule}
          enableReinitialize={true}
          validationSchema={AutoReplyRuleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveRule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Quando o cliente mandar uma mensagem com essa palavra/frase, o sistema
                  responde automaticamente pelo WhatsApp — mas só enquanto o atendimento
                  ainda estiver aguardando (sem ninguém atribuído). Se um atendente já
                  pegou o chamado, não interrompe mais.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Nome da regra"
                      name="name"
                      autoFocus
                      placeholder="Ex: Cobrança de terceiros"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Palavras ou frases"
                      name="keywordsText"
                      placeholder={"Uma por linha, pode colar direto. Ex:\nresponsável pelo financeiro\nimposto de renda"}
                      helperText="Uma palavra/frase por linha. Pode digitar ou colar (Ctrl+V) normalmente."
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Mensagem de resposta"
                      name="replyMessage"
                      placeholder="Ex: Olá, boa tarde. Aqui é do Escritório Contec Contabilidade..."
                      error={touched.replyMessage && Boolean(errors.replyMessage)}
                      helperText={touched.replyMessage && errors.replyMessage}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.blockContact}
                          onChange={e => setFieldValue("blockContact", e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Bloquear o contato automaticamente depois de responder"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.active}
                          onChange={e => setFieldValue("active", e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Regra ativa"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {ruleId ? "Salvar" : "Adicionar"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AutoReplyRuleModal;
